import * as THREE from 'three';
import { playSound } from '../../../utils/game/sounds';
import { createTokenMesh, createTokenButtonMesh, updateLifeHUD } from '../../../utils/game/cardMesh';
import { getSpawnPoint, SPAWN_LABELS, SPAWN_COLORS, getTrackerPositions, isTrackerConfigured, getTrackerTokenPosition } from '../../../utils/game/spawnConfig';
import { TRACKER_DEFS, PLAYERS, PLAYER_LABELS, valueToPositions } from '../../../utils/game/trackerConfig';
import { MANUAL_BUFF_DURATION, createManualManaEntry, OP } from '../../../utils/game/engine/automation';
import { msg } from '../../../utils/game/logMessage';

// Tracker tokens, tracker +/- buttons, life/mana HUD panels, and manual
// buff management, extracted from GameBoard.jsx.
//
// Host contract: props (isSpectating, isCasualSandbox, startingHp,
// arenaPlayerInfo, arenaOpponentInfo), state (gameState, spawnConfig,
// isPlacingSpawns, isHost, currentTurn, turnNumbers, hudPanel,
// hudScroll), setState, forceUpdate, scene, meshes, lifeHUDs,
// hoveredMesh, spawnMarkers, trackerPreviewMarkers, trackerTokenMeshes,
// trackerButtonMeshes, trackerCursorPreview, effectLedger,
// playerResources, sync, _broadcastTracker, _broadcastCardStat,
// _refreshMovementHUD, syncHoverStatusOverlay, _sweepDeadMinions,
// _findAvatarForPlayer, _trackerLogCoalescer, logAction,
// recalculateSiteAffinities, _demaskOnAvatarDamage.

// Texture URLs for the tracker tokens printed on the board. The SVGs are
// served by the Bun runtime out of dist/gameboard-tokens. `wind` maps to
// the `air-token.svg` asset. The two are interchangeable terms here.
const TOKEN_ASSET_BASE = '/game-assets/gameboard-tokens';
export const TRACKER_TOKEN_TEXTURES = {
  life: `${TOKEN_ASSET_BASE}/generic-token.svg`,
  mana: `${TOKEN_ASSET_BASE}/generic-token.svg`,
  earth: `${TOKEN_ASSET_BASE}/earth-token.svg`,
  water: `${TOKEN_ASSET_BASE}/water-token.svg`,
  fire: `${TOKEN_ASSET_BASE}/fire-token.svg`,
  wind: `${TOKEN_ASSET_BASE}/air-token.svg`,
};
export const TRACKER_BUTTON_TEXTURES = {
  plus: `${TOKEN_ASSET_BASE}/plus-token.svg`,
  minus: `${TOKEN_ASSET_BASE}/minus-token.svg`,
};

// --- Spawn Point Markers ---
export function updateSpawnMarkers(host, config) {
  // Remove old markers
  for (const [, mesh] of host.spawnMarkers) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    mesh.material.dispose();
  }
  host.spawnMarkers.clear();

  if (!host.state.isPlacingSpawns) return;

  for (const [key, label] of Object.entries(SPAWN_LABELS)) {
    const point = getSpawnPoint(config, key);
    const color = SPAWN_COLORS[key] || '#ffffff';

    const geo = new THREE.RingGeometry(1.5, 2, 32);
    const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, transparent: true, opacity: 0.7 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(point.x, 0.2, point.z);
    host.scene.scene.add(mesh);
    host.spawnMarkers.set(key, mesh);
  }
}

export function clearTrackerPreviews(host) {
  for (const mesh of host.trackerPreviewMarkers) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    mesh.material.dispose();
  }
  host.trackerPreviewMarkers = [];
}

export function trackerMeshKey(player, trackerKey, row) {
  return `${player}_${trackerKey}_${row || 'single'}`;
}

export function createTrackerTokens(host) {
  for (const [, mesh] of host.trackerTokenMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m?.dispose());
  }
  host.trackerTokenMeshes.clear();

  const { spawnConfig, gameState } = host.state;

  for (const player of PLAYERS) {
    for (const [trackerKey, def] of Object.entries(TRACKER_DEFS)) {
      if (def.disabled) continue;
      if (!isTrackerConfigured(spawnConfig, player, trackerKey, def)) continue;

      const value = gameState.trackers[player][trackerKey];
      const positions = valueToPositions(trackerKey, value);
      const topTexture = TRACKER_TOKEN_TEXTURES[trackerKey];

      for (const { row, posIndex } of positions) {
        const pos = getTrackerTokenPosition(spawnConfig, player, trackerKey, row, posIndex);
        if (!pos) continue;

        const meshKey = trackerMeshKey(player, trackerKey, row);
        const tokenInstance = {
          id: `tracker-${player}-${trackerKey}-${row || 'single'}`,
          x: pos.x,
          z: pos.z,
          color: 'red',
          topTexture,
          flip: player === 'p2',
        };
        const mesh = createTokenMesh(tokenInstance);
        host.scene.scene.add(mesh);
        host.trackerTokenMeshes.set(meshKey, mesh);
      }
    }
  }
}

export function updateTrackerTokenPositions(host) {
  const { spawnConfig, gameState } = host.state;

  for (const player of PLAYERS) {
    for (const [trackerKey, def] of Object.entries(TRACKER_DEFS)) {
      if (def.disabled) continue;
      const value = gameState.trackers[player][trackerKey];
      const positions = valueToPositions(trackerKey, value);

      for (const { row, posIndex } of positions) {
        const meshKey = trackerMeshKey(player, trackerKey, row);
        const mesh = host.trackerTokenMeshes.get(meshKey);
        if (!mesh) continue;

        const pos = getTrackerTokenPosition(spawnConfig, player, trackerKey, row, posIndex);
        if (pos) {
          mesh.position.x = pos.x;
          mesh.position.z = pos.z;
        }
      }
    }
  }
}

export function createTrackerButtons(host) {
  // Remove existing button meshes
  for (const [, mesh] of host.trackerButtonMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m?.dispose());
  }
  host.trackerButtonMeshes.clear();

  const { spawnConfig } = host.state;
  const btnRadius = 2.2;
  const btnHeight = 0.2;
  const btnRestY = btnHeight / 2 + 0.1;

  for (const player of PLAYERS) {
    for (const [trackerKey, def] of Object.entries(TRACKER_DEFS)) {
      if (def.disabled) continue;
      if (!isTrackerConfigured(spawnConfig, player, trackerKey, def)) continue;

      const data = getTrackerPositions(spawnConfig, player, trackerKey);
      if (!data) continue;

      // Element affinities: a single click pad at the first tracker
      // slot. Painted with the same elemental token glyph used by the
      // count tokens so the affordance stays visible at zero (and any
      // stacked tokens cleanly continue the same icon upward). Uses a
      // tighter radius than the life/mana +/- buttons because it has
      // to nest inside the playmat's affinity slot pill.
      if (def.integratedButton) {
        const btnPos = data[0];
        if (!btnPos) continue;

        const flip = player === 'p2';
        const mesh = createTokenButtonMesh({
          radius: 1.4,
          height: btnHeight,
          topTextureUrl: TRACKER_TOKEN_TEXTURES[trackerKey],
          flip,
        });
        mesh.position.set(btnPos.x, btnRestY, btnPos.z);
        mesh.userData = { type: 'trackerButton', action: 'integrated', player, trackerKey };
        host.scene.scene.add(mesh);
        host.trackerButtonMeshes.set(`${player}_${trackerKey}_integrated`, mesh);
        continue;
      }

      // Life/Mana: textured minus (-) and plus (+) buttons at computed offsets
      let firstPos, lastPos;
      if (def.rows) {
        firstPos = data.ones?.[0];
        lastPos = data.ones?.[data.ones.length - 1];
      } else {
        firstPos = data[0];
        lastPos = data[data.length - 1];
      }
      if (!firstPos || !lastPos) continue;

      const dx = lastPos.x - firstPos.x;
      const dz = lastPos.z - firstPos.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const nx = dx / len;
      const nz = dz / len;
      const offset = 4.6;

      // Inward offset toward the player's tens row (i.e. toward the
      // board center) so the +/- buttons sit a touch higher on screen
      // relative to each player's tracker row.
      const tensFirst = def.rows ? data.tens?.[0] : null;
      let upX = 0;
      let upZ = 0;
      if (tensFirst) {
        const ux = tensFirst.x - firstPos.x;
        const uz = tensFirst.z - firstPos.z;
        const ulen = Math.sqrt(ux * ux + uz * uz) || 1;
        upX = ux / ulen;
        upZ = uz / ulen;
      }
      const upShift = 2.1;

      const minusPos = {
        x: firstPos.x - nx * offset + upX * upShift,
        z: firstPos.z - nz * offset + upZ * upShift,
      };
      const plusPos = {
        x: lastPos.x + nx * offset + upX * upShift,
        z: lastPos.z + nz * offset + upZ * upShift,
      };

      const flip = player === 'p2';

      const minusMesh = createTokenButtonMesh({
        radius: btnRadius,
        height: btnHeight,
        topTextureUrl: TRACKER_BUTTON_TEXTURES.minus,
        flip,
      });
      minusMesh.position.set(minusPos.x, btnRestY, minusPos.z);
      minusMesh.userData = { type: 'trackerButton', action: 'decrement', player, trackerKey };
      host.scene.scene.add(minusMesh);
      host.trackerButtonMeshes.set(`${player}_${trackerKey}_minus`, minusMesh);

      const plusMesh = createTokenButtonMesh({
        radius: btnRadius,
        height: btnHeight,
        topTextureUrl: TRACKER_BUTTON_TEXTURES.plus,
        flip,
      });
      plusMesh.position.set(plusPos.x, btnRestY, plusPos.z);
      plusMesh.userData = { type: 'trackerButton', action: 'increment', player, trackerKey };
      host.scene.scene.add(plusMesh);
      host.trackerButtonMeshes.set(`${player}_${trackerKey}_plus`, plusMesh);
    }
  }
}

// Apply an in-HUD +/- button click on a minion's ATK or HP.
// Mutates the card instance, updates the HUD sprite, and broadcasts the
// new absolute value so both clients stay in sync (no action loss under
// multiple rapid clicks because each broadcast carries the final number).
export function applyLifeButton(host, { action, stat, cardId }) {
  const cardMesh = host.meshes.get(cardId);
  if (!cardMesh) return;
  const card = cardMesh.userData.cardInstance;
  const hud = host.lifeHUDs.get(cardId);
  if (stat === 'mov') {
    // v1.91. Manual override of `_movementBonus`. Engine reads
    // `1 + (card._movementBonus || 0)` as the per-turn step budget;
    // a printed Movement +2 card defaults to bonus=2 (3 steps).
    // Floor at 0 so the unit always keeps its base 1 step from the
    // Movement row. Immobile is the dedicated path to zero steps.
    if (action === 'increment') card._movementBonus = (card._movementBonus || 0) + 1;
    else card._movementBonus = Math.max(0, (card._movementBonus || 0) - 1);
    host._broadcastCardStat(cardId, 'mov', card._movementBonus);
    host._refreshMovementHUD(cardId);
    if (host.hoveredMesh?.userData?.cardInstance?.id === cardId) {
      host.syncHoverStatusOverlay();
    }
    return;
  }
  if (stat === 'atk') {
    if (action === 'increment') card.currentAttack = (card.currentAttack || 0) + 1;
    else card.currentAttack = Math.max(0, (card.currentAttack || 0) - 1);
    if (hud) {
      const display = card.currentAttack + (card._auraStatDelta || 0);
      updateLifeHUD(hud.sprite, display, 'atk', display - (card.baseAttack ?? display));
    }
    host._broadcastCardStat(cardId, 'atk', card.currentAttack);
  } else {
    // No clamp: manual DEF down behaves like dealing 1 damage, so
    // a 0-base minion can be killed by the +/- button too. The
    // sweep below applies the Sorcery damage-vs-power death rule
    // and removes the card if it's now marked for death.
    if (action === 'increment') card.currentLife = (card.currentLife || 0) + 1;
    else card.currentLife = (card.currentLife || 0) - 1;
    // A manual edit DEFINES the unit's intended life value. Unlike
    // damage (which heals at every end phase), a manual buff persists
    // until the user removes it. The end-of-turn heal restores to
    // base + this delta, so a 1/1 buffed to 6/6 that takes 5 combat
    // damage heals back to 6, not to its printed 1. Broadcast carries
    // manual:true so the other client records the same intent (plain
    // hp mirrors are damage and must not move this).
    card._manualLifeDelta = card.currentLife - (card.baseLife ?? 0);
    if (hud?.hpSprite) {
      const display = Math.max(0, card.currentLife) + (card._auraStatDelta || 0);
      updateLifeHUD(hud.hpSprite, display, 'hp', display - (card.baseLife ?? display));
    }
    host._broadcastCardStat(cardId, 'hp', card.currentLife, { manual: true });
    host._sweepDeadMinions();
  }
  if (host.hoveredMesh?.userData?.cardInstance?.id === cardId) {
    host.syncHoverStatusOverlay();
  }
}

// Absolute display name for a player ('p1' or 'p2'). Used by logbook
// text that needs to read identically on both clients. Relative
// pronouns ("your", "opponent's") would render differently per viewer
// and break the actor-as-source-of-truth invariant for log entries.
export function displayNameForPlayer(host, player) {
  if (host.props.isCasualSandbox) {
    const avatarCard = host._findAvatarForPlayer(player === 'p2' ? 2 : 1);
    return avatarCard?.name || PLAYER_LABELS[player] || player;
  }
  const localPlayer = host.state.isHost ? 'p1' : 'p2';
  const info = player === localPlayer
    ? host.props.arenaPlayerInfo
    : host.props.arenaOpponentInfo;
  return info?.name || PLAYER_LABELS[player] || player;
}

// Format a life-tracker change. Uses absolute player names rather than
// relative pronouns so the text is verbatim on both clients. Only the
// actor produces the line (via logAction) and broadcasts it; receivers
// never independently format from a state diff.
export function lifeLogText(host, player, prev, next) {
  const name = displayNameForPlayer(host, player);
  const delta = next - prev;
  const sign = delta > 0 ? `+${delta}` : `${delta}`;
  return msg("{name}'s life: {prev} → {next} ({sign})", { name, prev, next, sign });
}

export function buffDurationLabel(entry) {
  const exp = entry?.expiry;
  if (!exp || exp.type === 'permanent') return msg('permanent');
  if (exp.type === 'until_leaves') return msg('while in play');
  if (exp.type === 'turn_end') return msg('this turn');
  return '';
}

// Buff log text. Actor-perspective free so it renders identically on
// both clients (the logbook component prefixes the actor's name).
export function buffAddedLogText(entry) {
  const amount = entry?.effect?.args?.amount ?? 0;
  const sign = amount > 0 ? `+${amount}` : `${amount}`;
  return msg('Added a {sign} mana buff ({duration})', { sign, duration: buffDurationLabel(entry) });
}

export function buffClearedLogText(entry) {
  const amount = entry?.effect?.args?.amount ?? 0;
  const sign = amount > 0 ? `+${amount}` : `${amount}`;
  if (entry?.isManual) {
    return msg('Cleared a {sign} mana buff', { sign });
  }
  const name = entry?.sourceCardName || msg('a card');
  return msg('Cleared the {sign} mana buff from {name}', { sign, name });
}

// Visual + audio click acknowledgement for any tracker button (the
// four element pads and the life/mana +/-). Fires regardless of
// whether the underlying tracker actually changed, so the click
// feels responsive even on no-ops at the bounds.
export function flashTrackerButton(host, mesh) {
  if (!mesh?.material) return;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x8a6a2a); });
  playSound('uiClick');
  setTimeout(() => {
    const restoreHex = host.hoveredMesh === mesh ? 0x4a3a16 : 0x000000;
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(restoreHex); });
  }, 140);
}

export function incrementTracker(host, player, trackerKey) {
  if (host.props.isSpectating) return;
  const def = TRACKER_DEFS[trackerKey];
  const { gameState } = host.state;
  const prev = gameState.trackers[player][trackerKey];
  // Life is capped at the match's starting HP (10 in Quick Match,
  // 20 in Standard) instead of the generic 99 cap from TRACKER_DEFS
  // Healing past the starting total would defeat the format's
  // whole point. Other trackers (mana, element affinities) keep
  // the def-level cap.
  const cap = trackerKey === 'life' ? (host.props.startingHp || 20) : def.max;
  if (prev < cap) {
    gameState.trackers[player][trackerKey]++;
    updateTrackerTokenPositions(host);
    host.forceUpdate();
    host._broadcastTracker(player, trackerKey, gameState.trackers[player][trackerKey]);
    if (trackerKey === 'life') {
      host._trackerLogCoalescer.push({
        player, key: trackerKey,
        prev, next: prev + 1, actor: 'you',
      });
    }
  }
}

export function decrementTracker(host, player, trackerKey) {
  if (host.props.isSpectating) return;
  const { gameState } = host.state;
  const prev = gameState.trackers[player][trackerKey];
  if (prev > 0) {
    gameState.trackers[player][trackerKey]--;
    updateTrackerTokenPositions(host);
    host.forceUpdate();
    host._broadcastTracker(player, trackerKey, gameState.trackers[player][trackerKey]);
    if (trackerKey === 'life') {
      host._trackerLogCoalescer.push({
        player, key: trackerKey,
        prev, next: prev - 1, actor: 'you',
      });
      // No demask here. A manual life decrement can't tell Avatar
      // damage from a life payment / site hit, and the imposter only
      // sheds on actual Avatar damage. Players unmask deliberately via
      // the right-click "Take off mask" menu.
    }
  }
}

// ── HUD buff / life management ────────────────────────────────

export function openHudPanel(host, player, kind) {
  // Anchor to the whole player HUD rect (not just the clicked row)
  // so the popout panel can match its full vertical height and align
  // flush to its outer edge.
  const hudEl = document.querySelector(`[data-hud-panel-for="${player}"]`);
  if (!hudEl) return;
  const rect = hudEl.getBoundingClientRect();
  host.setState({ hudPanel: { player, kind, rect }, hudScroll: null });
}

export function closeHudPanel(host) {
  host.setState({ hudPanel: null });
}

export function canEditHudFor(host, player) {
  if (host.props.isSpectating) return false;
  if (host.props.isCasualSandbox) return true;
  const localPlayer = host.state.isHost ? 'p1' : 'p2';
  return player === localPlayer;
}

// Convert a viewport rect to canvas-local pixels for the HUD popup
// anchors. Subtracts the .app-canvas offset per the project's fixed-
// position-inside-canvas convention. Returns the rect's top-left in
// canvas-local space; the popup positions itself with its own top
// matching this top (so it flush-aligns with the HUD panel edge).
export function canvasLocalAnchor(rect) {
  if (!rect) return null;
  const canvas = document.querySelector('.app-canvas');
  const cRect = canvas?.getBoundingClientRect();
  const cw = cRect?.width || window.innerWidth;
  const ch = cRect?.height || window.innerHeight;
  const left = (cRect ? rect.left - cRect.left : rect.left);
  const top  = (cRect ? rect.top  - cRect.top  : rect.top);
  const bottom = top + rect.height;
  // Bottom-alignment uses CSS `bottom`, which inside .app-canvas
  // (a containing block via its translateZ(0)) is measured from the
  // canvas's bottom edge. Convert: distFromCanvasBottom = ch - bottom.
  const distFromCanvasBottom = ch - bottom;
  // Top-half HUD (opponent in MP, P1 in casual) → top-align so the
  // panel grows DOWN. Bottom-half HUD (local in MP, P2 in casual) →
  // bottom-align so the panel grows UP.
  const hudCenter = top + rect.height / 2;
  const edge = hudCenter < ch / 2 ? 'top' : 'bottom';
  return {
    left, top, bottom,
    width: rect.width, height: rect.height,
    canvasWidth: cw, canvasHeight: ch,
    distFromCanvasBottom,
    edge,
  };
}

export function handleHudScroll(host, player, kind, event) {
  if (!canEditHudFor(host, player)) return;
  event.preventDefault();
  event.stopPropagation();
  // 50ms accumulator clamps to ±1 per slice so a fast wheel spin
  // delivers smooth ±1 ticks rather than a ±5 jump.
  const now = Date.now();
  const last = host._lastHudScrollAt || 0;
  if (now - last < 50) return;
  host._lastHudScrollAt = now;
  const sign = event.deltaY < 0 ? 1 : -1;
  const rect = event.currentTarget.getBoundingClientRect();
  host.setState((s) => {
    const prev = s.hudScroll && s.hudScroll.player === player && s.hudScroll.kind === kind
      ? s.hudScroll.delta : 0;
    const nextDelta = prev + sign;
    return {
      hudScroll: { player, kind, delta: nextDelta, rect },
      hudPanel: null,
    };
  });
}

export function commitHudScroll(host) {
  const s = host.state.hudScroll;
  if (!s || s.delta === 0) { host.setState({ hudScroll: null }); return; }
  if (s.kind === 'life') {
    adjustLife(host, s.player, s.delta);
  } else {
    addManualManaBuff(host, s.player, s.delta, MANUAL_BUFF_DURATION.THIS_TURN);
  }
  host.setState({ hudScroll: null });
}

export function cancelHudScroll(host) {
  host.setState({ hudScroll: null });
}

export function adjustLife(host, player, delta) {
  const step = delta > 0 ? 1 : -1;
  const count = Math.abs(delta);
  for (let i = 0; i < count; i++) {
    if (step > 0) incrementTracker(host, player, 'life');
    else decrementTracker(host, player, 'life');
  }
}

export function addManualManaBuff(host, player, amount, duration, n) {
  if (!canEditHudFor(host, player)) return;
  if (!Number.isInteger(amount) || amount === 0) return;
  const entry = createManualManaEntry({
    controller: player,
    amount,
    duration,
    n,
    activePlayer: host.state.currentTurn,
    turnNumbers: host.state.turnNumbers,
  });
  const id = host.effectLedger.add(entry);
  const stored = { id, ...entry };
  host.logAction(buffAddedLogText(stored));
  // Mid-turn: bump the current mana pool by the buff amount so the
  // user sees the effect immediately. _stepProvideMana only fires
  // at turn start, so without this the HUD stays stale until pass.
  // Positive bump grants (caps at MAX); negative bump pays (floors
  // at 0). Engine class handles broadcast + HUD refresh.
  host.playerResources.bumpMana(player, amount);
  host.recalculateSiteAffinities('p1');
  host.recalculateSiteAffinities('p2');
}

export function removeBuffEntry(host, entryId) {
  const entry = host.effectLedger.entries.find((e) => e.id === entryId);
  if (!entry) return;
  if (!canEditHudFor(host, entry.controller)) return;
  host.logAction(buffClearedLogText(entry));
  const amt = entry.effect?.args?.amount || 0;
  host.effectLedger.removeById(entryId);
  // Symmetric to add: drop the buff amount out of the current mana
  // pool too. bumpMana floors at 0 / caps at MAX; engine class
  // handles broadcast + HUD refresh.
  if (amt !== 0 && entry.effect?.op === OP.GRANT_MANA) {
    host.playerResources.bumpMana(entry.controller, -amt);
  }
  host.recalculateSiteAffinities('p1');
  host.recalculateSiteAffinities('p2');
  host.forceUpdate();
}

export function addTrackerPreview(host, x, z) {
  const geometry = new THREE.CylinderGeometry(1.75, 1.75, 0.35, 32);
  const material = new THREE.MeshBasicMaterial({ color: 0xf97316, transparent: true, opacity: 0.5 });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, 0.3, z);
  host.scene.scene.add(mesh);
  host.trackerPreviewMarkers.push(mesh);
}

export function showTrackerCursorPreview(host) {
  if (host.trackerCursorPreview) return;
  const geometry = new THREE.CylinderGeometry(1.75, 1.75, 0.35, 32);
  const material = new THREE.MeshBasicMaterial({ color: 0xf97316, transparent: true, opacity: 0.35 });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(0, 0.3, 0);
  mesh.visible = false;
  host.scene.scene.add(mesh);
  host.trackerCursorPreview = mesh;
}

export function hideTrackerCursorPreview(host) {
  if (!host.trackerCursorPreview) return;
  host.scene.scene.remove(host.trackerCursorPreview);
  host.trackerCursorPreview.geometry.dispose();
  host.trackerCursorPreview.material.dispose();
  host.trackerCursorPreview = null;
}
